import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  MessageCircle,
  Smartphone,
  Mail,
  AtSign,
  Download,
  Link2,
  QrCode,
  Loader2,
  Check,
  ChevronRight,
  X,
} from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useSolarConfig } from "@/hooks/useSolarConfig";
import { fetchTenantBranding, type TenantBranding } from "@/lib/tenant-branding";
import {
  QuickQuotePdfTemplate,
  type QuickQuotePdfData,
} from "./QuickQuotePdfTemplate";
import {
  generateQuotePdf,
  uploadQuotePdf,
  blobToBase64,
} from "@/lib/quote-pdf";
import type { QuickQuote } from "@/hooks/useQuickQuotes";

interface QuickQuoteSendSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: QuickQuote | null;
}

type ActionId =
  | "wa_system"
  | "wa_device"
  | "email_system"
  | "email_device"
  | "download"
  | "copy"
  | null;

function normalizePhoneBR(raw: string | null | undefined): string {
  return (raw || "").replace(/\D/g, "");
}

export function QuickQuoteSendSheet({
  open,
  onOpenChange,
  quote,
}: QuickQuoteSendSheetProps) {
  const isMobile = useIsMobile();
  const { user, currentTenant } = useAuth();
  const { solarConfig, configLoaded } = useSolarConfig();
  const pdfRef = useRef<HTMLDivElement>(null);

  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [seller, setSeller] = useState<{
    name: string | null;
    phone: string | null;
    email: string | null;
  }>({ name: null, phone: null, email: null });

  const [loadingAction, setLoadingAction] = useState<ActionId>(null);
  const [copyDone, setCopyDone] = useState(false);
  const [showQr, setShowQr] = useState(false);

  // E-mail input fallback
  const [emailDraft, setEmailDraft] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(false);

  const publicUrl = useMemo(() => {
    if (!quote?.public_slug) return null;
    return `${window.location.origin}/p/cotacao/${quote.public_slug}`;
  }, [quote?.public_slug]);

  // Carrega branding + dados do vendedor ao abrir
  useEffect(() => {
    if (!open || !currentTenant?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const b = await fetchTenantBranding(currentTenant.id);
        if (!cancelled) setBranding(b);
      } catch (err) {
        console.error("[send-sheet] branding error", err);
      }
      // Vendedor: profiles do usuário atual
      try {
        if (user?.id) {
          const { data } = await supabase
            .from("profiles")
            .select("full_name, phone, email")
            .eq("user_id", user.id)
            .maybeSingle();
          if (!cancelled && data) {
            setSeller({
              name: (data as any).full_name ?? null,
              phone: (data as any).phone ?? null,
              email: (data as any).email ?? user.email ?? null,
            });
          } else if (!cancelled) {
            setSeller({
              name: user.user_metadata?.full_name ?? null,
              phone: null,
              email: user.email ?? null,
            });
          }
        }
      } catch {
        // silent
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, currentTenant?.id, user?.id, user?.email]);

  // Reset estados ao fechar
  useEffect(() => {
    if (!open) {
      setLoadingAction(null);
      setCopyDone(false);
      setShowQr(false);
      setShowEmailInput(false);
      setEmailDraft("");
    }
  }, [open]);

  const pdfData: QuickQuotePdfData | null = useMemo(() => {
    if (!quote || !branding) return null;
    return {
      quote,
      tenant: {
        name: branding.companyName || currentTenant?.name || "Empresa",
        logo_url: branding.logoUrl,
        logo_url_white: branding.logoUrlWhite,
        brand_primary_color: branding.primaryColor,
        // Só passa a tarifa quando vier do DB (configLoaded). Caso contrário,
        // null força derivedQuoteFinancials a cair no fallback com warn no
        // console, em vez de mascarar com o 0.90 do DEFAULT_SOLAR_CONFIG.
        default_tariff: configLoaded ? solarConfig.defaultTariff : null,
      },
      seller,
      publicUrl,
    };
  }, [quote, branding, seller, publicUrl, currentTenant?.name, configLoaded, solarConfig.defaultTariff]);

  // Acoes ----------------------------------------------------------

  async function withPdfBlob<T>(
    fn: (blob: Blob, filename: string) => Promise<T>,
  ): Promise<T> {
    if (!pdfRef.current || !quote) {
      throw new Error("Template PDF não disponível");
    }
    const result = (await generateQuotePdf(
      pdfRef.current,
      quote.code,
      "blob",
    )) as { blob: Blob; filename: string };
    return fn(result.blob, result.filename);
  }

  async function handleDownload() {
    if (!pdfRef.current || !quote) return;
    setLoadingAction("download");
    try {
      await generateQuotePdf(pdfRef.current, quote.code, "download");
      toast.success("PDF baixado");
    } catch (err) {
      toast.error("Erro ao gerar PDF", {
        description: (err as any)?.message || String(err),
      });
    } finally {
      setLoadingAction(null);
    }
  }

  async function handleCopy() {
    if (!publicUrl) {
      toast.warning("Gere o link público antes de compartilhar");
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopyDone(true);
      toast.success("Link copiado");
      setTimeout(() => setCopyDone(false), 2000);
    } catch {
      toast.error("Não foi possível copiar");
    }
  }

  async function handleWaSystem() {
    if (!quote || !currentTenant?.id) return;
    if (!publicUrl) {
      toast.warning("Gere o link público antes de enviar");
      return;
    }
    setLoadingAction("wa_system");
    try {
      // 1. Gera PDF + upload
      const pdfUrl = await withPdfBlob(async (blob, filename) => {
        return uploadQuotePdf(blob, filename, currentTenant.id);
      });

      // 2. Busca conversa pelo telefone
      const phoneNorm =
        quote.client_phone_normalized ||
        normalizePhoneBR(quote.client_phone);

      let convoId: string | null = null;
      if (phoneNorm.length >= 8) {
        const { data: convs } = await supabase
          .from("wa_conversations")
          .select("id, contact_phone, last_message_at")
          .eq("tenant_id", currentTenant.id)
          .ilike("contact_phone", `%${phoneNorm}`)
          .order("last_message_at", { ascending: false })
          .limit(1);
        convoId = (convs?.[0] as any)?.id ?? null;
      }

      const message = `Olá ${quote.client_name || ""}! Segue sua cotação solar ${quote.code}: ${publicUrl}`;

      if (convoId) {
        const { data, error } = await supabase.functions.invoke(
          "wa-send-message",
          {
            body: {
              conversation_id: convoId,
              type: "document",
              media_url: pdfUrl,
              media_filename: `cotacao-${quote.code.toLowerCase()}.pdf`,
              content: message,
            },
          },
        );
        if (error) throw error;
        if ((data as any)?.error) throw new Error((data as any).error);

        await supabase
          .from("quick_quotes" as any)
          .update({
            whatsapp_sent_at: new Date().toISOString(),
            sent_at: quote.sent_at ?? new Date().toISOString(),
            status: quote.status === "draft" ? "sent" : quote.status,
          } as any)
          .eq("id", quote.id);

        toast.success("PDF enviado no WhatsApp");
      } else {
        // Fallback: abre wa.me com link
        const phone = phoneNorm.startsWith("55") ? phoneNorm : `55${phoneNorm}`;
        const text = encodeURIComponent(message);
        window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
        toast.info("Conversa não encontrada. Abrimos o WhatsApp com o link.");
      }
    } catch (err) {
      toast.error("Erro ao enviar pelo WhatsApp", {
        description: (err as any)?.message || String(err),
      });
    } finally {
      setLoadingAction(null);
    }
  }

  function handleWaDevice() {
    if (!quote) return;
    const phoneNorm =
      quote.client_phone_normalized || normalizePhoneBR(quote.client_phone);
    if (!phoneNorm) {
      toast.warning("Telefone do cliente ausente");
      return;
    }
    if (!publicUrl) {
      toast.warning("Gere o link público antes");
      return;
    }
    const phone = phoneNorm.startsWith("55") ? phoneNorm : `55${phoneNorm}`;
    const text = encodeURIComponent(
      `Olá ${quote.client_name || ""}! Segue sua cotação solar ${quote.code}: ${publicUrl}`,
    );
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
  }

  async function handleEmailSystem(forceEmail?: string) {
    if (!quote) return;
    const recipient = forceEmail || quote.client_email || emailDraft;
    if (!recipient) {
      setShowEmailInput(true);
      return;
    }
    if (!publicUrl) {
      toast.warning("Gere o link público antes de enviar");
      return;
    }
    setLoadingAction("email_system");
    try {
      const { blob, filename } = (await generateQuotePdf(
        pdfRef.current!,
        quote.code,
        "blob",
      )) as { blob: Blob; filename: string };
      const pdf_base64 = await blobToBase64(blob);

      const { data, error } = await supabase.functions.invoke(
        "send-quote-email",
        {
          body: {
            quote_id: quote.id,
            pdf_base64,
            pdf_filename: filename,
            recipient_email: recipient,
            recipient_name: quote.client_name || "cliente",
            seller_name: seller.name,
            tenant_name:
              branding?.companyName || currentTenant?.name || "Empresa",
            quote_code: quote.code,
            quote_link: publicUrl,
            tenant_logo_url: branding?.logoUrl ?? null,
            tenant_primary_color: branding?.primaryColor ?? null,
          },
        },
      );
      if (error) throw error;
      if ((data as any)?.ok === false) {
        throw new Error((data as any)?.message || "Falha ao enviar e-mail");
      }
      toast.success("E-mail enviado!");
      setShowEmailInput(false);
      setEmailDraft("");
    } catch (err) {
      toast.error("Erro ao enviar e-mail", {
        description: (err as any)?.message || String(err),
      });
    } finally {
      setLoadingAction(null);
    }
  }

  function handleEmailDevice() {
    if (!quote) return;
    const email = quote.client_email;
    if (!email) {
      toast.warning("Cliente sem e-mail cadastrado");
      return;
    }
    const subject = encodeURIComponent(
      `Cotação Solar ${quote.code} – ${branding?.companyName || ""}`,
    );
    const body = encodeURIComponent(
      `Olá ${quote.client_name || ""}!\n\nSegue sua cotação solar ${quote.code}.\n\nVeja online: ${publicUrl ?? ""}\n\nAtenciosamente,\n${seller.name || ""}`,
    );
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  }

  if (!quote) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={isMobile ? "bottom" : "right"}
        className={
          isMobile
            ? "h-[85vh] p-0 flex flex-col gap-0"
            : "w-full sm:max-w-md p-0 flex flex-col gap-0"
        }
      >
        <SheetHeader className="px-6 py-4 border-b">
          <SheetTitle>Enviar cotação</SheetTitle>
          <SheetDescription>
            <span className="font-mono font-semibold">{quote.code}</span>
            {quote.client_name ? ` • ${quote.client_name}` : ""}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-2 py-3">
          {!configLoaded && (
            <div className="mx-4 mb-2 px-3 py-2 rounded-md bg-muted/60 text-xs text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" />
              Carregando configurações de tarifa…
            </div>
          )}
          {/* WHATSAPP */}
          <SectionLabel>WhatsApp</SectionLabel>
          <ActionRow
            icon={<MessageCircle className="h-5 w-5" />}
            title="WhatsApp pelo sistema"
            subtitle={
              configLoaded
                ? "Envia o PDF direto no chat"
                : "Carregando configurações…"
            }
            loading={loadingAction === "wa_system"}
            disabled={!!loadingAction || !configLoaded}
            onClick={handleWaSystem}
          />
          <ActionRow
            icon={<Smartphone className="h-5 w-5" />}
            title="WhatsApp deste dispositivo"
            subtitle="Abre o app com a mensagem pronta"
            loading={false}
            disabled={!!loadingAction}
            onClick={handleWaDevice}
          />

          <Separator className="my-3" />

          {/* E-MAIL */}
          <SectionLabel>E-mail</SectionLabel>
          <ActionRow
            icon={<Mail className="h-5 w-5" />}
            title="E-mail pelo sistema"
            subtitle={
              !configLoaded
                ? "Carregando configurações…"
                : quote.client_email
                  ? `Para ${quote.client_email}`
                  : "Envia com PDF anexado"
            }
            loading={loadingAction === "email_system"}
            disabled={!!loadingAction || !configLoaded}
            onClick={() => handleEmailSystem()}
          />
          {showEmailInput && !quote.client_email && (
            <div className="px-4 pb-3 pt-1 flex items-center gap-2">
              <Input
                type="email"
                placeholder="email@cliente.com"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                className="flex-1"
              />
              <Button
                size="sm"
                onClick={() => handleEmailSystem(emailDraft)}
                disabled={!emailDraft.includes("@") || !!loadingAction || !configLoaded}
              >
                {loadingAction === "email_system" ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  "Enviar"
                )}
              </Button>
            </div>
          )}
          <ActionRow
            icon={<AtSign className="h-5 w-5" />}
            title="E-mail deste dispositivo"
            subtitle="Abre seu app de e-mail"
            loading={false}
            disabled={!!loadingAction}
            onClick={handleEmailDevice}
          />

          <Separator className="my-3" />

          {/* OUTROS */}
          <SectionLabel>Outros</SectionLabel>
          <ActionRow
            icon={<Download className="h-5 w-5" />}
            title="Baixar PDF"
            subtitle={
              configLoaded
                ? "Salva o arquivo no dispositivo"
                : "Carregando configurações…"
            }
            loading={loadingAction === "download"}
            disabled={!!loadingAction || !configLoaded}
            onClick={handleDownload}
          />
          <ActionRow
            icon={
              copyDone ? (
                <Check className="h-5 w-5 text-success" />
              ) : (
                <Link2 className="h-5 w-5" />
              )
            }
            title="Copiar link"
            subtitle={publicUrl ?? "Sem link público"}
            loading={false}
            disabled={!!loadingAction || !publicUrl}
            onClick={handleCopy}
          />
          <ActionRow
            icon={<QrCode className="h-5 w-5" />}
            title="QR Code"
            subtitle="Para compartilhar presencialmente"
            loading={false}
            disabled={!!loadingAction || !publicUrl}
            onClick={() => setShowQr((v) => !v)}
          />
          {showQr && publicUrl && (
            <div className="mx-4 my-2 p-4 border rounded-lg bg-surface flex flex-col items-center gap-3">
              <QRCodeSVG value={publicUrl} size={200} level="M" />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowQr(false)}
              >
                <X className="h-4 w-4 mr-1" /> Fechar QR
              </Button>
            </div>
          )}
        </div>

        <div className="px-6 py-3 border-t">
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => onOpenChange(false)}
            disabled={!!loadingAction}
          >
            Fechar
          </Button>
        </div>

        {/* Template PDF pré-renderizado, oculto fora da tela */}
        {pdfData && (
          <div
            style={{
              position: "fixed",
              left: "-99999px",
              top: 0,
              opacity: 0,
              pointerEvents: "none",
            }}
            aria-hidden="true"
          >
            <QuickQuotePdfTemplate ref={pdfRef} data={pdfData} />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─────────────── Sub-components ───────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function ActionRow({
  icon,
  title,
  subtitle,
  loading,
  disabled,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  loading: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left disabled:opacity-50 disabled:cursor-not-allowed"
    >
      <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center text-foreground shrink-0">
        {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium text-foreground">{title}</div>
        {subtitle && (
          <div className="text-xs text-muted-foreground truncate">{subtitle}</div>
        )}
      </div>
      <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
    </button>
  );
}
